import { Layout, UploadedFile } from '@web-kc/shared/components';
import { useTranslation } from 'next-i18next';
import Typography from '@mui/material/Typography';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useApp } from '@web-kc/core/providers';

type DocumentFile = {
  name: string;
  url: string;
  size: number;
  extension: string;
};

const codecsFile: Record<string, DocumentFile> = {
  en: {
    name: 'Code of Conduct.pdf',
    url: '/documents/Code%20of%20Conduct.pdf',
    size: 480579,
    extension: '.pdf',
  },
  ru: {
    name: 'Кодекс поведения.pdf',
    url: '/documents/Кодекс%20поведения.pdf',
    size: 494675,
    extension: '.pdf',
  },
  kk: {
    name: 'Әдеп кодексі.pdf',
    url: '/documents/Әдеп%20кодексі.pdf',
    size: 511922,
    extension: '.pdf',
  },
};

const conflictsFile: Record<string, DocumentFile> = {
  en: {
    name: 'Conflict-of-Interest-Policy_eng.pdf',
    url: '/documents/Conflict-of-Interest-Policy_eng.pdf',
    size: 657785,
    extension: '.pdf',
  },
  ru: {
    name: 'Политика по урегулированию конфликта интересов.pdf',
    url: '/documents/Политика%20по%20урегулированию%20конфликта%20интересов.pdf',
    size: 685965,
    extension: '.pdf',
  },
  kk: {
    name: 'Мүдделер қайшылығын реттеу саясаты.pdf',
    url: '/documents/Мүдделер%20қайшылығын%20реттеу%20саясаты.pdf',
    size: 811939,
    extension: '.pdf',
  },
};

const agreementFile: Record<string, DocumentFile> = {
  en: {
    name: 'Agreement on the Use of the Information System for Electronic Procurement of the Air Astana JSC group of companies.pdf',
    url: '/documents/Agreement on the Use of the Information System for Electronic Procurement of the Air Astana JSC group of companies.pdf',
    size: 836890,
    extension: '.pdf',
  },
  ru: {
    name: 'Соглашение об использовании Информационной системы электронных закупок группы компаний АО «Эйр Астана».pdf',
    url: '/documents/Соглашение об использовании Информационной системы электронных закупок группы компаний АО «Эйр Астана».pdf',
    size: 836890,
    extension: '.pdf',
  },
  kk: {
    name: '«Эйр Астана» АҚ компаниялар тобының Электрондық сатып алудың ақпараттық жүйесін пайдалану туралы келісім.pdf',
    url: '/documents/«Эйр Астана» АҚ компаниялар тобының Электрондық сатып алудың ақпараттық жүйесін пайдалану туралы келісім.pdf',
    size: 836890,
    extension: '.pdf',
  },
};

const regulationFile: Record<string, DocumentFile> = {
  en: {
    name: 'Regulation of personal data AirAstana-Mp.pdf',
    url: '/documents/Regulation.pdf',
    size: 196015,
    extension: '.pdf',
  },
  ru: {
    name: 'Положение персональных данных ЭА-Mp.pdf',
    url: '/documents/ПоложениеПД_ЭА-Mp.pdf',
    size: 238479,
    extension: '.pdf',
  },
};

const supplierCodeFile: Record<string, DocumentFile> = {
  en: {
    name: 'Supplier code of conduct.pdf',
    url: '/documents/Supplier_Code_of_Conduct.pdf',
    size: 108078,
    extension: '.pdf',
  },
  ru: {
    name: 'Кодекс поставщика.pdf',
    url: '/documents/Кодекс_Поставщика.pdf',
    size: 118217,
    extension: '.pdf',
  },
  kk: {
    name: 'Жеткізуші кодексі.pdf',
    url: '/documents/Жеткізуші_Кодексі.pdf',
    size: 118186,
    extension: '.pdf',
  },
};

const orderSanctionsFile: Record<string, DocumentFile> = {
  en: {
    name: 'Air_Astana_Order_Sanctions_Terms_ENG_compliance.pdf',
    url: '/documents/Air_Astana_Order_Sanctions_Terms_ENG_compliance.pdf',
    size: 1104298,
    extension: '.pdf',
  },
  ru: {
    name: 'Air_Astana_Order_Sanctions_Terms_RUS_compliance.pdf',
    url: '/documents/Air_Astana_Order_Sanctions_Terms_RUS_compliance.pdf',
    size: 1664346,
    extension: '.pdf',
  },
  kk: {
    name: 'Air_Astana_Order_Sanctions_Terms_KAZ_compliance.pdf',
    url: '/documents/Air_Astana_Order_Sanctions_Terms_KAZ_compliance.pdf',
    size: 1629919,
    extension: '.pdf',
  },
};

const esgFile: Record<string, DocumentFile> = {
  en: {
    name: 'Sustainability ESG Procurement Policy Statement.pdf',
    url: '/documents/Sustainability_ESG_Procurement_Policy_Statement.pdf',
    size: 275963,
    extension: '.pdf',
  },
  ru: {
    name: 'Заявление о политике устойчивых и ESG закупок.pdf',
    url: '/documents/Заявление_о_политике_устойчивых_и_ESG_закупок.pdf',
    size: 344218,
    extension: '.pdf',
  },
  kk: {
    name: 'Тұрақты және ESG қағидаттарына негізделген сатып алу саясаты туралы.pdf',
    url: '/documents/Тұрақты_және_ESG_қағидаттарына_негізделген_сатып_алу_саясаты_туралы.pdf',
    size: 345160,
    extension: '.pdf',
  },
};

export function PagesDocuments() {
  const { t } = useTranslation();
  const { locale = 'ru' } = useRouter();
  const isKc = useApp() === 'kc';

  const {
    conflict,
    codecs,
    agreement,
    regulation,
    supplierCode,
    orderSanctions,
    esg,
  } = useMemo(
    () => ({
      conflict: conflictsFile[locale] ?? conflictsFile['ru'],
      codecs: codecsFile[locale] ?? codecsFile['ru'],
      agreement: agreementFile[locale] ?? agreementFile['ru'],
      regulation: regulationFile[locale] ?? regulationFile['ru'],
      supplierCode: supplierCodeFile[locale] ?? supplierCodeFile['ru'],
      orderSanctions: orderSanctionsFile[locale] ?? orderSanctionsFile['ru'],
      esg: esgFile[locale] ?? esgFile['ru'],
    }),
    [locale],
  );

  return (
    <Layout
      paths={['/', '/documents']}
      labels={[`${t('breadcrumbs.main')}`, '']}
      pageTitle={t(`common.tenders_${isKc ? 'kc' : 'rti'}`, { ns: 'common' })}
    >
      <Typography variant="h2" mb={4}>
        {t('documents:agreementTitle')}
      </Typography>

      <Typography fontSize={18} mb={2}>
        {t('documents:agreementDocsText')}:
      </Typography>

      <UploadedFile
        file={agreement}
        sx={{ backgroundColor: 'white', width: 'fit-content', mb: 2 }}
        downloadFunc={() => {
          window.open(agreement.url, '_blank');
          return Promise.resolve();
        }}
      />
      <UploadedFile
        file={regulation}
        sx={{ backgroundColor: 'white', width: 'fit-content', mb: 4 }}
        downloadFunc={() => {
          window.open(regulation.url, '_blank');
          return Promise.resolve();
        }}
      />

      <Typography fontSize={18} mb={2}>
        {t('documents:additionalDocsText')}:
      </Typography>

      <UploadedFile
        file={conflict}
        sx={{ backgroundColor: 'white', width: 'fit-content', mb: 2 }}
        downloadFunc={() => {
          window.open(conflict.url, '_blank');
          return Promise.resolve();
        }}
      />
      <UploadedFile
        file={codecs}
        sx={{ backgroundColor: 'white', width: 'fit-content', mb: 2 }}
        downloadFunc={() => {
          window.open(codecs.url, '_blank');
          return Promise.resolve();
        }}
      />
      <UploadedFile
        file={supplierCode}
        sx={{ backgroundColor: 'white', width: 'fit-content', mb: 2 }}
        downloadFunc={() => {
          window.open(supplierCode.url, '_blank');
          return Promise.resolve();
        }}
      />
      <UploadedFile
        file={orderSanctions}
        sx={{ backgroundColor: 'white', width: 'fit-content', mb: 4 }}
        downloadFunc={() => {
          window.open(orderSanctions.url, '_blank');
          return Promise.resolve();
        }}
      />

      <Typography fontSize={18} mb={2}>
        {t('documents:esgDocsText')}:
      </Typography>

      <UploadedFile
        file={esg}
        sx={{ backgroundColor: 'white', width: 'fit-content', mb: 4 }}
        downloadFunc={() => {
          window.open(esg.url, '_blank');
          return Promise.resolve();
        }}
      />
    </Layout>
  );
}
